/**
 * Which questions in a conversation have already been answered.
 *
 * An option is a plain input (CHARTER §7.139 ⑤): pressing one submits its value
 * the way typing it would. So every question ever asked kept its buttons, live,
 * and a press on a two-rounds-old one sent that value again to an agent that
 * had moved on -- with no sign to the person that this had happened (the
 * report's D305).
 *
 * The record says which one was pressed: the input the press became carries
 * `choice` -- the reply it answered and the value pressed (`reply_choice`, put
 * there by `service.submit_message`). That is the whole reading (§7.194 ④).
 * It used to be widened to "the next thing the owner said, when it is one of
 * the values that question offered", which is a guess dressed as a fact: an
 * owner who typed the same word as a button had not pressed it, and a
 * question answered in the owner's own words is still open. Neither is called
 * answered here. What is not on the input is not known.
 */

import type { ChatMessage } from "../lib/api";

/** Reply id -> the option value the owner answered it with. */
function choices(messages: ChatMessage[]) {
  const answered: Record<string, string> = {};
  const labels: Record<string, string> = {};
  const questions = new Map<string, ChatMessage>();
  for (const message of messages) {
    if (message.role === "user" && message.kind === "message") {
      if (!message.choice) continue;
      const question = questions.get(message.choice.reply_id);
      const option = question?.options.find(o => o.value === message.choice!.value);
      answered[message.choice.reply_id] = message.choice.value;
      // The question may have left the server's bounded message window; the
      // input still says what it answered, in the words it was offered.
      labels[message.id] = option?.label ?? message.choice.label;
      continue;
    }
    if (
      message.kind === "reply" &&
      message.state === "sent" &&
      !message.payload?.approval &&
      message.options?.length
    ) {
      questions.set(message.id, message);
    }
  }
  return { answered, labels };
}

export function answeredOptions(messages: ChatMessage[]): Record<string, string> {
  return choices(messages).answered;
}

export function optionAnswerLabels(messages: ChatMessage[]): Record<string, string> {
  return choices(messages).labels;
}
